import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import CustomButton from "../../components/common/CustomButton";
import { useUserProfile, useUpdateUserProfile, useBlobToken } from "../../custom-hooks/queries";
import { toast } from "react-toastify";
import { uploadFile } from "../../utils/HelperMethod";

// Skeleton loader
const EditProfileSkeleton = () => (
  <div className="w-full max-w-[1200px] mx-auto px-6 py-10 animate-pulse">
    <div className="flex items-center justify-between">
      <div>
        <div className="h-8 w-40 bg-gray-200 rounded"></div>
        <div className="h-4 w-64 bg-gray-200 rounded mt-2"></div>
      </div>
      <div className="flex gap-4">
        <div className="h-10 w-24 bg-gray-200 rounded"></div>
        <div className="h-10 w-32 bg-gray-200 rounded"></div>
      </div>
    </div>
    <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
      <div className="lg:col-span-2 border rounded-2xl p-8">
        <div className="grid grid-cols-2 gap-6">
          {[...Array(4)].map((_, i) => (
            <div key={i}>
              <div className="h-4 w-24 bg-gray-200 rounded"></div>
              <div className="h-10 w-full bg-gray-200 rounded mt-2"></div>
            </div>
          ))}
        </div>
      </div>
      <div className="border rounded-2xl p-8 flex flex-col items-center">
        <div className="h-24 w-24 rounded-full bg-gray-200"></div>
        <div className="h-10 w-32 bg-gray-200 rounded mt-6"></div>
      </div>
    </div>
  </div>
);

export default function EditProfilePageUser() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const fileInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    phone: "",
    profileUrl: "",
  });
  const [previewImage, setPreviewImage] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Fetch user profile
  const { data, isLoading } = useUserProfile();
  const profile = data?.data || data || {};

  // Update profile mutation
  const { mutate: updateProfile, isPending: isUpdating } = useUpdateUserProfile();

  // Blob token for file upload
  const { mutateAsync: getBlobToken } = useBlobToken();

  // Populate form when profile data loads
  useEffect(() => {
    if (profile.firstName) {
      setFormData({
        firstName: profile.firstName || "",
        lastName: profile.lastName || "",
        phone: profile.phone || "",
        profileUrl: profile.profileImageUrl || "",
      });
      setPreviewImage(profile.profileImageUrl || null);
    }
  }, [profile.firstName, profile.lastName, profile.phone, profile.profileImageUrl]);

  // Handle input change
  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Handle image selection
  const handleImageSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreviewImage(URL.createObjectURL(file));
    }
  };

  // Handle form submit
  const handleSubmit = async () => {
    try {
      let profileUrl = formData.profileUrl;

      // Upload new image if selected
      if (selectedFile) {
        setIsUploading(true);
        try {
          const tokenResponse = await getBlobToken({
            uploadEnitityType: "UserProfile",
          });
          const uploadedUrl = await uploadFile(
            selectedFile,
            tokenResponse?.data?.sasUrl || tokenResponse?.sasUrl
          );
          profileUrl = uploadedUrl;
        } catch (uploadError) {
          console.error("Image upload failed:", uploadError);
          toast.error("Failed to upload image. Please try again.");
          setIsUploading(false);
          return;
        }
        setIsUploading(false);
      }

      // Update profile
      updateProfile(
        {
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: formData.phone,
          profileUrl: profileUrl,
        },
        {
          onSuccess: () => {
            toast.success("Profile updated successfully!");
            queryClient.invalidateQueries(["userProfile"]);
            navigate("/user/profile");
          },
          onError: (error) => {
            toast.error(error?.response?.data?.message || "Failed to update profile");
          },
        }
      );
    } catch (error) {
      toast.error("Something went wrong. Please try again.");
    }
  };

  if (isLoading) {
    return <EditProfileSkeleton />;
  }

  const isSubmitting = isUpdating || isUploading;

  return (
    <div className="w-full max-w-[1200px] mx-auto px-6 py-10">
      {/* HEADER ROW */}
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold">Edit Profile</h1>
          <p className="text-gray-500 mt-1">Update your profile information</p>
        </div>

        {/* BUTTONS */}
        <div className="flex items-center gap-4">
          <CustomButton
            variant="secondary"
            onClick={() => navigate("/user/profile")}
            disabled={isSubmitting}
          >
            Cancel
          </CustomButton>

          <CustomButton onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting ? "Saving..." : "Save & Submit"}
          </CustomButton>
        </div>
      </div>

      {/* FORM WRAPPER */}
      <div className="mt-10 grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* LEFT — FORM */}
        <div className="lg:col-span-2 border border-gray-200 rounded-2xl p-8 shadow-sm">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* FIRST NAME */}
            <div>
              <label className="font-medium text-gray-700">First Name</label>
              <input
                type="text"
                name="firstName"
                value={formData.firstName}
                onChange={handleChange}
                placeholder="Enter Your First Name"
                className="mt-2 w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#00C3CC]"
              />
            </div>

            {/* LAST NAME */}
            <div>
              <label className="font-medium text-gray-700">Last Name</label>
              <input
                type="text"
                name="lastName"
                value={formData.lastName}
                onChange={handleChange}
                placeholder="Enter Your Last Name"
                className="mt-2 w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#00C3CC]"
              />
            </div>

            {/* EMAIL (Read-only) */}
            <div>
              <label className="font-medium text-gray-700">Email</label>
              <input
                type="email"
                value={profile.email || ""}
                disabled
                className="mt-2 w-full px-4 py-2 border rounded-lg bg-gray-100 text-gray-500 cursor-not-allowed"
              />
              <p className="text-xs text-gray-400 mt-1">Email cannot be changed</p>
            </div>

            {/* PHONE */}
            <div>
              <label className="font-medium text-gray-700">Phone (optional)</label>
              <input
                type="text"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                placeholder="Enter Your Phone Number"
                className="mt-2 w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-[#00C3CC]"
              />
            </div>
          </div>
        </div>

        {/* RIGHT — PROFILE IMAGE PANEL */}
        <div className="border border-gray-200 rounded-2xl p-8 shadow-sm flex flex-col items-center">
          <div className="h-24 w-24 rounded-full bg-[#001F2D] flex items-center justify-center overflow-hidden">
            {previewImage ? (
              <img
                src={previewImage}
                className="h-full w-full object-cover"
                alt="Profile"
                onError={(e) => {
                  e.target.style.display = "none";
                }}
              />
            ) : (
              <span className="text-white text-3xl font-semibold">
                {formData.firstName?.[0]?.toUpperCase() || "U"}
              </span>
            )}
          </div>

          <input
            type="file"
            ref={fileInputRef}
            onChange={handleImageSelect}
            accept="image/*"
            className="hidden"
          />

          <button
            onClick={() => fileInputRef.current?.click()}
            disabled={isSubmitting}
            className="mt-6 border border-gray-300 rounded-lg px-6 py-2 hover:bg-gray-50 text-gray-700 disabled:opacity-50"
          >
            {isUploading ? "Uploading..." : "Change Photo"}
          </button>

          {selectedFile && (
            <p className="text-sm text-gray-500 mt-2 text-center">
              {selectedFile.name}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
